import { getCookie } from '../services';
import { isServer } from './server.helper';
import { GetLocal } from './storage.helper';

/**
 * @function getIsHosted
 * @description Resolves whether the app is running in "hosted" (embedded/MFE) mode.
 * Precedence:
 *   1. `isHosted` query-string param (if a URLSearchParams instance is passed)
 *   2. `isHosted` value in localStorage
 *   3. Domain rule: on the client, when `window.location.hostname` is NOT a
 *      "native" booking-engine host, the app is treated as hosted and `'true'`
 *      is returned. A hostname is considered native when it either starts with
 *      `booking.` (e.g. `booking.livat.com`) or contains `.booking.` as a
 *      subdomain segment (e.g. `qa.booking.livat.com`, `dev.booking.livat.com`).
 *      Match is case-insensitive.
 *   4. Otherwise `false`.
 *
 * The string `'true'` (not a boolean) is returned for the hosted branches so
 * that downstream comparisons like `isHosted === 'true'` keep working.
 *
 * SSR-safe: skips the `window` lookup when `isServer` is true.
 *
 * @param {URLSearchParams} [queryString] Optional URLSearchParams to inspect for an `isHosted` query param.
 * @returns {'true' | string | false} `'true'` when hosted, the raw query/localStorage value when explicitly set, or `false`.
 */
export const getIsHosted = (queryString) => {
    const fromQuery = queryString?.get?.('isHosted');
    if (fromQuery) {
        return fromQuery;
    }

    const fromStorage = getCookie('isHosted');
    if (fromStorage) {
        return fromStorage;
    }

    if (!isServer && typeof window !== 'undefined') {
        const hostname = window?.location?.hostname?.toLowerCase() || '';
        console.log("hostname: ", hostname);
        const isNativeBookingHost =
            hostname &&
            (hostname.startsWith('booking.') || hostname.includes('.booking.')) 
            || hostname === 'localhost';
        if (hostname && !isNativeBookingHost) {
            return 'true';
        }
    }

    return false;
};
